import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { TokenManager } from '../../../shared/utils/token-manager';

interface Alert {
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
}

export const SignIn: React.FC = () => {
    const navigate = useNavigate();
    const { signIn } = useAuth();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [alert, setAlert] = useState<Alert>({ type: 'success', message: '' });
    const [showAlert, setShowAlert] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

    const validate = (): boolean => {
        const newErrors: { email?: string; password?: string } = {};
        
        if (!email) {
            newErrors.email = 'Email address is required';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        
        if (!password) {
            newErrors.password = 'Password is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validate()) {
            return;
        }

        setIsLoading(true);
        setShowAlert(false);

        try {
            const response = await signIn({ email, password });
            const accessToken = localStorage.getItem('accessToken');
            
            if (accessToken) {
                navigate('/dashboard');
            } else {
                console.error('Access token not found in localStorage');
                console.error('Response structure:', response);
                setIsLoading(false);
                setShowAlert(true);
                setAlert({
                    type: 'error',
                    message: 'Login failed: Token not received'
                });
            }
        } catch (error: any) {
            setIsLoading(false);
            
            // Reset form
            setPassword('');

            // Extract error message from API response
            let errorMessage = 'Wrong email or password';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setAlert({
                type: 'error',
                message: errorMessage
            });
            setShowAlert(true);
        }
    };

    const fieldStyles = {
        '& .MuiInputLabel-root': {
            color: '#ffffff',
            // Label ko white box se bahar nikalne ke liye
            transform: 'translate(0px, -20px) scale(0.85)', 
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#ffffff',
        },
        '& .MuiInputLabel-shrink': {
            // Forcefully label ko upar hi rakhne ke liye
            transform: 'translate(0px, -20px) scale(0.85) !important',
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '12px',
            height: '50px', // Uniform height
            '& fieldset': {
                borderColor: 'transparent',
            },
            '&:hover fieldset': {
                borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'transparent',
            },
        },
        '& .MuiInputBase-input': {
            color: '#1f2937',
            padding: '12px 14px',
        },
        // Error helper text ka color adjust karne ke liye
        '& .MuiFormHelperText-root': {
            color: '#ff8a8a', 
            marginBottom: '10px'
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#e6f7f0] to-[#ffe5d4] relative overflow-hidden flex flex-col items-center justify-center min-w-0 px-4 py-8 before:content-[''] before:absolute before:inset-0 before:opacity-10 before:bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(255,255,255,0.1)_10px,rgba(255,255,255,0.1)_20px)] before:pointer-events-none">
            <div className="bg-gray-800 rounded-3xl shadow-[0_12px_48px_rgba(74,107,143,0.25),0_4px_16px_rgba(0,0,0,0.1)] relative z-10 backdrop-blur-[10px] border border-white/10 w-full max-w-md px-6 py-8 sm:px-10 sm:py-12">
                {/* Logo */}
                <div className="flex justify-center mb-6">
                    <img 
                        src="/assets/images/logo/KOKI-logo.svg" 
                        alt="KOKI Logo"
                        className="h-12 sm:h-16"
                    />
                </div>

                {/* Welcome Text */}
                <div className="text-center mb-2">
                    <h1 className="text-white text-4xl font-bold leading-tight">Welcome Back</h1>
                    <p className="text-[#f0f0f0] text-sm mt-2">Enter your information on field below</p>
                </div>

                {/* Alert */}
                {showAlert && (
                    <div className={`mt-6 -mb-4 p-4 rounded-lg ${
                        alert.type === 'error' ? 'bg-red-100 text-red-700' : 
                        alert.type === 'success' ? 'bg-green-100 text-green-700' : 
                        'bg-blue-100 text-blue-700'
                    }`}>
                        {alert.message}
                    </div>
                )}

                {/* Sign in form */}
                <form className="mt-8" onSubmit={handleSubmit}>
                     {/* Email field */}
            <TextField
                className="w-full mt-6" // mt-6 diya hai taaki label ke liye jagah bane
                label="Email address"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                disabled={isLoading}
                fullWidth
                required
                InputLabelProps={{
                    shrink: true, // Label hamesha upar rahega
                }}
                sx={fieldStyles}
            />

            {/* Password field */}
            <TextField
                className="w-full mt-8" // Extra margin taaki upar wali field se gap rahe
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!errors.password}
                helperText={errors.password || ' '}
                disabled={isLoading}
                fullWidth
                required
                InputLabelProps={{
                    shrink: true,
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton 
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={fieldStyles}
            />


                    {/* Submit button */}
                    <AppButton
                        type="submit"
                        variant="primary"
                        fullWidth
                        loading={isLoading}
                        sx={{ mt: 3, py: 1.5, borderRadius: '12px' }}
                    >
                        Sign In
                    </AppButton>

                </form>
            </div>
        </div>
    );
};
